import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Bell, BellOff, BellRing, MessageSquare, X, Wifi, WifiOff, Ban } from 'lucide-react';
import { Avatar } from './ChatHeader';

const timeLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  const diff = (now - d) / 86400000;
  return diff < 7 ? d.toLocaleDateString('it-IT', { weekday: 'short' }) : d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
};

const Highlight = ({ text, q }) => {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  const start = Math.max(0, i - 24);
  return <>{start > 0 && '…'}{text.slice(start, i)}<mark className="bg-chartreuse/30 text-foreground rounded px-0.5">{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
};

function PushButton({ state, onToggle }) {
  const map = {
    on: { I: BellRing, label: 'Notifiche attive', cls: 'text-chartreuse border-chartreuse/40 bg-chartreuse/10' },
    off: { I: Bell, label: 'Attiva notifiche', cls: 'text-foreground border-border hover:border-chartreuse/50' },
    denied: { I: BellOff, label: 'Notifiche bloccate', cls: 'text-muted-foreground border-border' },
    unsupported: { I: BellOff, label: 'Notifiche non supportate', cls: 'text-muted-foreground border-border' },
  };
  const { I, label, cls } = map[state] || map.off;
  return (
    <button onClick={onToggle} data-testid="push-toggle-btn" title={label}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-[11px] font-semibold transition-colors ${cls}`}>
      <I size={13} className={state === 'off' ? 'animate-pulse' : ''}/> <span className="hidden sm:inline lg:hidden xl:inline">{label}</span>
    </button>
  );
}

export default function ConversationList({ convs, cid, tab, setTab, query, setQuery, results, onlineUsers, wsConnected, pushState, onTogglePush }) {
  const q = query.trim().toLowerCase();
  const list = convs
    .filter(c => (tab === 'archived' ? c.archived : !c.archived))
    .filter(c => !q || [c.other_user?.name, c.last_message, c.listing?.title, c.batch?.fruit_type].some(s => s?.toLowerCase().includes(q)));
  const nameOf = (id) => convs.find(c => c.conversation_id === id)?.other_user?.name || 'Conversazione';
  const archivedCount = convs.filter(c => c.archived).length;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="p-4 pb-3 space-y-3 border-b border-border">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl tracking-tight">Messaggi</h1>
          <span data-testid="ws-status" title={wsConnected ? 'Connesso in tempo reale' : 'Riconnessione in corso'}
                className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold ${wsConnected ? 'text-chartreuse' : 'text-muted-foreground'}`}>
            {wsConnected ? <><Wifi size={10}/> Live</> : <><WifiOff size={10}/> Offline</>}
          </span>
          <div className="ml-auto"><PushButton state={pushState} onToggle={onTogglePush}/></div>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca persone, annunci o messaggi…"
                 data-testid="chat-search-input"
                 className="w-full h-10 rounded-full bg-background border border-border pl-9 pr-9 text-sm outline-none focus:ring-1 focus:ring-chartreuse"/>
          {query && <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="chat-search-clear" aria-label="Cancella"><X size={14}/></button>}
        </div>
        <div className="relative flex p-1 rounded-full bg-secondary/60 text-xs font-semibold">
          {[['active', 'Attive'], ['archived', `Archiviate${archivedCount ? ` · ${archivedCount}` : ''}`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`chat-tab-${k}`}
                    className={`relative flex-1 h-8 rounded-full transition-colors ${tab === k ? 'text-obsidian' : 'text-muted-foreground hover:text-foreground'}`}>
              {tab === k && <motion.span layoutId="chatTab" className="absolute inset-0 rounded-full bg-chartreuse" transition={{ type: 'spring', damping: 26, stiffness: 340 }}/>}
              <span className="relative">{l}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {list.length === 0 && !results.length && (
          <div className="p-8 text-sm text-muted-foreground text-center" data-testid="chat-list-empty">
            <MessageSquare size={30} className="mx-auto mb-3 opacity-40" strokeWidth={1.4}/>
            <div className="font-medium text-foreground mb-1">{q ? 'Nessun risultato' : tab === 'archived' ? 'Nessuna chat archiviata' : 'Nessuna conversazione'}</div>
            {!q && tab === 'active' && <div className="text-xs">Contatta un proprietario dalla pagina di un annuncio per iniziare a chattare.</div>}
          </div>
        )}
        <motion.div initial="h" animate="s" variants={{ s: { transition: { staggerChildren: 0.04 } } }}>
          {list.map(c => {
            const active = cid === c.conversation_id;
            return (
              <motion.div key={c.conversation_id} variants={{ h: { opacity: 0, x: -10 }, s: { opacity: 1, x: 0 } }}>
                <Link to={`/messaggi/${c.conversation_id}`} data-testid={`conv-${c.conversation_id}`}
                      className={`relative flex items-center gap-3 px-4 py-3 transition-colors ${active ? 'bg-secondary' : 'hover:bg-secondary/50'}`}>
                  {active && <motion.span layoutId="convActive" className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-chartreuse"/>}
                  <Avatar user={c.other_user} size={44} online={onlineUsers.has(c.other_user?.user_id)}/>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`truncate text-sm ${c.unread ? 'font-bold' : 'font-medium'}`}>{c.other_user?.name || '—'}</span>
                      {c.blocked_by_me && <Ban size={11} className="text-destructive shrink-0"/>}
                      <span className="ml-auto text-[10px] text-muted-foreground shrink-0">{timeLabel(c.updated_at)}</span>
                    </div>
                    {(c.listing || c.batch) && (
                      <div className="text-[10px] uppercase tracking-wider text-chartreuse/80 truncate mt-0.5">
                        {c.listing?.title || `${c.batch.fruit_type}${c.batch.variety ? ` ${c.batch.variety}` : ''}`}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs truncate ${c.unread ? 'text-foreground' : 'text-muted-foreground'}`}>{c.last_message}</span>
                      {c.unread > 0 && <span className="ml-auto shrink-0 min-w-5 h-5 px-1.5 grid place-items-center text-[10px] rounded-full bg-chartreuse text-obsidian font-bold" data-testid={`conv-unread-${c.conversation_id}`}>{c.unread}</span>}
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {results.length > 0 && (
          <div className="border-t border-border mt-1" data-testid="chat-search-results">
            <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.24em] font-bold text-muted-foreground">Nei messaggi</div>
            {results.map(r => (
              <Link key={r.message_id} to={`/messaggi/${r.conversation_id}?m=${r.message_id}`} data-testid={`search-hit-${r.message_id}`}
                    className="block px-4 py-2.5 hover:bg-secondary/50 transition-colors">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-medium truncate">{nameOf(r.conversation_id)}</span>
                  <span className="ml-auto text-[10px] text-muted-foreground">{timeLabel(r.created_at)}</span>
                </div>
                <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5"><Highlight text={r.content} q={query.trim()}/></div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
